/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { getStorage } from 'src/core/utils/getStorage';
import { AnalyticsStatusOptions } from 'src/luis/projects/analytics/types/AnalyticsStatus';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { IntentTrainingResult } from 'src/luis/projects/analytics/types/IntentTrainingResult';
import { AnalyticsStore } from 'src/luis/projects/analytics/types/stores/AnalyticsStore';
import { TrainingResultMetadata } from 'src/luis/projects/analytics/types/TrainingResultMetadata';
import { LuisId } from 'src/luis/types/Resource';

/**
 * An analytics store factory object.
 *
 * @param analytics The analytics store to create if exist else initialize one.
 */
export const createAnalyticsStore = (analytics: Partial<AnalyticsStore>): AnalyticsStore => {
    let minThreshold: number = 10;
    const alpha: number = 1;
    const thresholdOfIntentsWithLowUtterances: number = 95;
    const thresholdOfIntentsWithHighUtterances: number = 98;

    const store: AnalyticsStore = {
        serviceStatus: 'notStarted',
        lastTrainingDate: analytics.lastTrainingDate,
        timeElapsed: analytics.timeElapsed,
        intentsCount: analytics.intentsCount,
        entitiesCount: analytics.entitiesCount,
        patternsCount: analytics.patternsCount,
        utterancesCount: analytics.utterancesCount,
        phraseListsCount: analytics.phraseListsCount,
        correctUtterancesCount: analytics.correctUtterancesCount,
        unclearUtterancesCount: analytics.unclearUtterancesCount,
        incorrectUtterancesCount: analytics.incorrectUtterancesCount,
        unclearIncorrectUtterancesCount: analytics.unclearIncorrectUtterancesCount,
        utterancesPerIntent: analytics.utterancesPerIntent,
        intentsTrainingResult: new Map<LuisId, IntentTrainingResult>(),
        appEndpointHits: new Map<string, number>(),

        /**
         * Gets the needy intent for the current active version of the current app.
         */
        get needyIntents() {
            if (this.serviceStatus !== 'completed' || !this.utterancesPerIntent) {
                return [];
            }

            const mean = this.utterancesCount / this.intentsCount;
            const intents = Array.from(this.utterancesPerIntent.values()).sort((a, b) => (a.numUtterances < b.numUtterances ? -1 : 1));
            const standardDeviation = getStandardDeviation(intents, mean, this.intentsCount);
            minThreshold = Math.min(minThreshold, mean);
            const minUnbalancedThreshold = Math.max(minThreshold, mean - alpha * standardDeviation);

            return intents.filter(
                intent => intent.correctAccuracy < thresholdOfIntentsWithLowUtterances && intent.numUtterances < minUnbalancedThreshold
            );
        },

        /**
         * Gets the greedy intent for the current active version of the current app.
         */
        get greedyIntents() {
            if (this.serviceStatus !== 'completed' || !this.utterancesPerIntent) {
                return [];
            }

            const mean = this.utterancesCount / this.intentsCount;
            const intents = Array.from(this.utterancesPerIntent.values()).sort((a, b) => (a.numUtterances > b.numUtterances ? -1 : 1));
            const standardDeviation = getStandardDeviation(intents, mean, this.intentsCount);
            const maxUnbalancedThreshold = mean + alpha * standardDeviation;

            return intents.filter(
                intent => intent.correctAccuracy < thresholdOfIntentsWithHighUtterances && intent.numUtterances > maxUnbalancedThreshold
            );
        },

        /**
         * Gets the incorrect intent for the current active version of the current app.
         */
        get incorrectIntents() {
            if (this.serviceStatus !== 'completed' || !this.utterancesPerIntent) {
                return [];
            }

            return Array.from(this.utterancesPerIntent.values())
                .filter(intent => intent.incorrectAccuracy !== 0)
                .sort((a, b) => (a.incorrectAccuracy < b.incorrectAccuracy ? 1 : -1));
        },

        /**
         * Gets the unclear intent for the current active version of the current app.
         */
        get unclearIntents() {
            if (this.serviceStatus !== 'completed' || !this.utterancesPerIntent) {
                return [];
            }

            return Array.from(this.utterancesPerIntent.values())
                .filter(intent => intent.unclearAccuracy !== 0)
                .sort((a, b) => (a.unclearAccuracy < b.unclearAccuracy ? 1 : -1));
        },

        /**
         * Sets the status of the analytics calculation on the backend side.
         *
         * @param status The new status of the analytics calculations
         */
        setServiceStatus(status: AnalyticsStatusOptions) {
            this.serviceStatus = status;
        },

        /**
         * A function to set the differences whenever a comparison is required.
         *
         * @param comparedAnalyticsStore The analytics store of the compared version.
         */
        setComparedVersionDifferences(comparedAnalyticsStore: AnalyticsStore) {
            const currentCorrect = (this.correctUtterancesCount * 100) / this.utterancesCount;
            const currentUnclear = (this.unclearUtterancesCount * 100) / this.utterancesCount;
            const currentIncorrect = (this.incorrectUtterancesCount * 100) / this.utterancesCount;
            const compareWithCorrect = (comparedAnalyticsStore.correctUtterancesCount * 100) / comparedAnalyticsStore.utterancesCount;
            const compareWithUnclear = (comparedAnalyticsStore.unclearUtterancesCount * 100) / comparedAnalyticsStore.utterancesCount;
            const compareWithIncorrect = (comparedAnalyticsStore.incorrectUtterancesCount * 100) / comparedAnalyticsStore.utterancesCount;

            this.comparedVersionDifferences = {
                correctAccuracy: Number((currentCorrect - compareWithCorrect).toFixed(1)),
                unclearAccuracy: Number((currentUnclear - compareWithUnclear).toFixed(1)),
                incorrectAccuracy: Number((currentIncorrect - compareWithIncorrect).toFixed(1)),
                numIntents: this.intentsCount - comparedAnalyticsStore.intentsCount,
                numEntities: this.entitiesCount - comparedAnalyticsStore.entitiesCount,
                numUtterances: this.utterancesCount - comparedAnalyticsStore.utterancesCount
            };
        },

        /**
         * Sets the metadata of the analytics store.
         *
         * @param metadata The metadata of the analytics.
         */
        setMetadata(metadata: TrainingResultMetadata) {
            this.lastTrainingDate = metadata.lastTrainingDate;
            this.timeElapsed = metadata.timeElapsed;
            this.intentsCount = metadata.intentsCount;
            this.entitiesCount = metadata.entitiesCount;
            this.patternsCount = metadata.patternsCount;
            this.utterancesCount = metadata.utterancesCount;
            this.phraseListsCount = metadata.phraseListsCount;
            this.correctUtterancesCount = metadata.correctUtterancesCount;
            this.unclearUtterancesCount = metadata.unclearUtterancesCount;
            this.incorrectUtterancesCount = metadata.incorrectUtterancesCount;
            this.unclearIncorrectUtterancesCount = metadata.unclearIncorrectUtterancesCount;
            this.utterancesPerIntent = metadata.utterancesPerIntent;
        },

        /**
         * Sets the application endpoint hits map.
         *
         * @param map The new endpoint hits map.
         */
        setAppEndpointHits(map: Map<string, number>) {
            this.appEndpointHits = map;
        },

        /**
         * Adds the given training data for the given intent id.
         *
         * @param intentId The id of the intent owns the given training data.
         * @param intent The intent that owns the given training data.
         */
        addIntentTrainingResult(intentId: LuisId, intent: IntentTrainingResult) {
            this.intentsTrainingResult.set(intentId, intent);
        },

        /**
         * Invalidates the current store data.
         */
        invalidate() {
            this.serviceStatus = 'outdated';
            this.intentsTrainingResult.clear();

            getStorage('sessionStorage').setData('analyticsCardsTracker', null);
            getStorage('sessionStorage').setData('analyticsIntentsWithErrorsCardTracker', null);
        }
    };

    return observable(
        store,
        {
            serviceStatus: observable,
            appEndpointHits: observable,
            intentsTrainingResult: observable,
            needyIntents: computed,
            greedyIntents: computed,
            unclearIntents: computed,
            incorrectIntents: computed,
            invalidate: action,
            setMetadata: action,
            setServiceStatus: action,
            setAppEndpointHits: action,
            addIntentTrainingResult: action,
            setComparedVersionDifferences: action
        },
        { deep: false }
    );
};

const getStandardDeviation = (arr: IntentMetadata[], mean: number, total: number): number =>
    Math.sqrt(arr.map(intent => (intent.numUtterances - mean) ** 2).reduce((sum, value) => sum + value, 0) / total);
